import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import { setTenantState, selectTenantState } from '../store/slices/tenantSlice';
import type { RootState } from '../store';

/**
 * Hook pour state management isolé par tenant.
 * Chaque subscription a son propre namespace dans Redux.
 */
export const useTenantState = <T = any>(key: string, initialValue?: T) => {
  const dispatch = useDispatch();
  const { currentSubscription } = useSubscriptionContext();
  
  // Construire la clé tenant
  const tenantKey = currentSubscription ? `sub_${currentSubscription.id}` : null;
  
  // Récupérer la valeur actuelle
  const value = useSelector((state: RootState) => {
    if (!tenantKey) return initialValue;
    return selectTenantState(state, `${tenantKey}_${key}`) ?? initialValue;
  });
  
  // Setter avec isolation tenant
  const setValue = useCallback((newValue: T) => {
    if (tenantKey) {
      dispatch(setTenantState({
        key: `${tenantKey}_${key}`,
        value: newValue
      }));
    }
  }, [dispatch, tenantKey, key]);
  
  return [value, setValue] as const;
};

/**
 * Hook pour localStorage isolé par tenant.
 */
export const useTenantLocalStorage = <T = any>(key: string, initialValue?: T) => {
  const { currentSubscription } = useSubscriptionContext();
  
  // Construire la clé avec namespace tenant
  const tenantKey = currentSubscription ? `tenant_${currentSubscription.id}_${key}` : key;
  
  const getValue = (): T => {
    try {
      const item = localStorage.getItem(tenantKey);
      return item ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue as T;
    }
  };
  
  const setValue = useCallback((value: T) => {
    try {
      localStorage.setItem(tenantKey, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [tenantKey]);
  
  const removeValue = useCallback(() => {
    localStorage.removeItem(tenantKey);
  }, [tenantKey]);
  
  return {
    value: getValue(),
    setValue,
    removeValue
  };
};
